import moment from 'moment';
import {EventInfo, EventSeriesInfo} from '@/interfaces/eventInfo.interface';
import {Holiday} from '@/interfaces/holiday.interface';
import {HolidayTypes} from '@/enums/HolidayTypes.enum';
import {StateCodes} from '@/enums/StateCodes.enum';
import {VacationGetterApi} from '@/classes/api/vacation-getter.api.class';
import {EventManagerApi} from '@/classes/api/event-manager.api.class';
import {CreateEventInfoRequestDto} from '@/classes/dto/event-manager/request/CreateEventInfo.request.dto';
import {EventInfoSeriesBase} from '@/classes/dto/event-manager/request/CreateEventInfoSeries.request.dto';
import {CreateEventInfoSeriesResponseDto} from '@/classes/dto/event-manager/response/EventInfoSeries.response.dto';
import {apiErrorMessage, idString} from '@/components/trainings/trainings.helper';

export const MAX_SERIES_DATES = 52;
const DAY = 'YYYY-MM-DD';
const LAST_STATE_KEY = 'trainings.series.holidayState';

export type RepeatMode = 'once' | 'weekly';
export type RepeatEnd = 'count' | 'until';

export type RepeatValue = {
    mode: RepeatMode;
    /** Every n weeks (1 or 2) */
    interval: number;
    end: RepeatEnd;
    /** Number of dates including the first one */
    count: number;
    until: Date | null;
};

export type SeriesDateRow = {
    key: string;
    /** Generated date (YYYY-MM-DD) this row came from, null for manually added dates */
    origin: string | null;
    day: Date;
    /** HH:mm */
    time: string;
    dayEdited: boolean;
    timeEdited: boolean;
    /** Excluded from creating ("überspringen" / removed) */
    skipped: boolean;
    /** Holiday conflict reviewed ("trotzdem") */
    ack: boolean;
};

export type HolidayHit = { label: string; school: boolean };

export function defaultRepeat(): RepeatValue {
    return {mode: 'once', interval: 1, end: 'count', count: 6, until: null};
}

let rowCounter = 0;

function newKey(): string {
    rowCounter += 1;
    return `row-${Date.now()}-${rowCounter}`;
}

/** Dates (YYYY-MM-DD) of a weekly pattern, starting with the first day, at most MAX_SERIES_DATES */
export function generateDays(first: Date | null, repeat: RepeatValue): string[] {
    if (!first || repeat.mode === 'once') {
        return [];
    }
    const interval = Math.max(1, Math.round(repeat.interval || 1));
    const start = moment(first).startOf('day');
    const days: string[] = [];
    if (repeat.end === 'until') {
        if (!repeat.until) {
            return [];
        }
        const until = moment(repeat.until).endOf('day');
        for (const current = start.clone(); current.isSameOrBefore(until) && days.length < MAX_SERIES_DATES; current.add(interval, 'weeks')) {
            days.push(current.format(DAY));
        }
        return days;
    }
    const count = Math.min(MAX_SERIES_DATES, Math.max(1, Math.round(repeat.count || 1)));
    for (let i = 0; i < count; i++) {
        days.push(start.clone().add(i * interval, 'weeks').format(DAY));
    }
    return days;
}

export function newRow(day: Date, time: string, origin: string | null): SeriesDateRow {
    return {key: newKey(), origin, day: moment(day).startOf('day').toDate(), time, dayEdited: false, timeEdited: false, skipped: false, ack: false};
}

/**
 * Regenerates the rows of a pattern while keeping manual edits: rows are matched by their generated date,
 * edited day/time, skipped and reviewed flags stay. Manually added rows are kept as well.
 * lost = edited rows whose generated date is no longer part of the pattern.
 */
export function mergeRows(rows: SeriesDateRow[], days: string[], time: string): { rows: SeriesDateRow[]; lost: number } {
    const byOrigin = new Map(rows.filter((row) => row.origin).map((row) => [row.origin as string, row]));
    const merged: SeriesDateRow[] = days.map((day) => {
        const existing = byOrigin.get(day);
        if (!existing) {
            return newRow(moment(day, DAY).toDate(), time, day);
        }
        return {
            ...existing,
            day: existing.dayEdited ? existing.day : moment(day, DAY).toDate(),
            time: existing.timeEdited ? existing.time : time,
        };
    });
    const lost = rows.filter((row) => row.origin && !days.includes(row.origin) && (row.dayEdited || row.timeEdited || row.skipped)).length;
    merged.push(...rows.filter((row) => !row.origin));
    return {rows: sortRows(merged), lost};
}

export function rowStart(row: Pick<SeriesDateRow, 'day' | 'time'>): Date {
    const [h, m] = (row.time || '00:00').split(':').map((el) => Number(el) || 0);
    return moment(row.day).startOf('day').hours(h).minutes(m).toDate();
}

export function sortRows(rows: SeriesDateRow[]): SeriesDateRow[] {
    return [...rows].sort((a, b) => rowStart(a).getTime() - rowStart(b).getTime());
}

export function rowDayKey(row: Pick<SeriesDateRow, 'day'>): string {
    return moment(row.day).format(DAY);
}

export function holidayLabel(holiday: Holiday): string {
    const name = (holiday.name || '').trim() || (holiday.type === HolidayTypes.SCHOOL ? 'Ferien' : 'Feiertag');
    if (holiday.type === HolidayTypes.SCHOOL && holiday.stateCode && !name.includes(' ')) {
        return `${name} ${holiday.stateCode}`;
    }
    return name;
}

/** School and public holidays on a day (holiday end is inclusive) */
export function holidaysOn(day: Date, holidays: Holiday[]): HolidayHit[] {
    const key = moment(day).format(DAY);
    const hits: HolidayHit[] = [];
    for (const holiday of holidays) {
        if (moment(holiday.start).format(DAY) <= key && key <= moment(holiday.end).format(DAY)) {
            const label = holidayLabel(holiday);
            if (!hits.some((hit) => hit.label === label)) {
                hits.push({label, school: holiday.type === HolidayTypes.SCHOOL});
            }
        }
    }
    // Public holidays first: they usually mean the venue is closed
    return hits.sort((a, b) => Number(a.school) - Number(b.school));
}

/** School holidays (Ferien) and public holidays (Feiertage) of a state in a period (vacation-getter) */
export async function loadStateHolidays(state: StateCodes, from: Date, to: Date): Promise<Holiday[]> {
    const resp = await VacationGetterApi.find({
        skipPagination: true,
        filter: {
            stateCode: [state],
            range: {start: moment(from).startOf('day').toDate(), end: moment(to).endOf('day').toDate()},
        },
    });
    // Filtered here (not via filter.type) so no extra field permission is needed; location specific entries are ignored
    return (resp?.holidays || []).filter((holiday) => holiday.type === HolidayTypes.SCHOOL || holiday.type === HolidayTypes.PUBLIC);
}

export function lastHolidayState(): StateCodes | null {
    try {
        const value = localStorage.getItem(LAST_STATE_KEY);
        return value && (Object.values(StateCodes) as string[]).includes(value) ? (value as StateCodes) : null;
    } catch (e) {
        return null;
    }
}

export function storeHolidayState(state: StateCodes) {
    try {
        localStorage.setItem(LAST_STATE_KEY, state);
    } catch (e) {
        // storage not available, only a convenience
    }
}

/** True when the backend does not know the route yet (older event-manager) */
export function isMissingEndpoint(e: unknown): boolean {
    const err = e as { response?: { status?: number }; status?: number; statusCode?: number };
    const status = err?.response?.status || err?.status || err?.statusCode;
    return status === 404 || status === 405;
}

export function seriesOf(training: Partial<EventInfo> | null | undefined): EventSeriesInfo | null {
    const series = training?.series;
    return series && series.id && series.total ? series : null;
}

export function seriesId(training: Partial<EventInfo> | null | undefined): string {
    return idString(seriesOf(training)?.id);
}

export function seriesLabel(training: Partial<EventInfo> | null | undefined): string {
    const series = seriesOf(training);
    return series ? `Termin ${series.index} von ${series.total}` : '';
}

export type SeriesCreateResult = CreateEventInfoSeriesResponseDto & {
    /** true when the backend had no series endpoint and the trainings were created one by one (without series link) */
    fallback: boolean;
};

/** Creates the trainings of a series; falls back to single creates on an older backend */
export async function createTrainingSeries(base: CreateEventInfoRequestDto, dates: Date[]): Promise<SeriesCreateResult> {
    const shared: Partial<CreateEventInfoRequestDto> = {...base};
    delete shared.startTime;
    delete shared.participantList;
    try {
        const resp = await EventManagerApi.createEventInfoSeries({base: shared as EventInfoSeriesBase, dates});
        return {
            seriesId: resp?.seriesId || null,
            created: resp?.created || [],
            failed: resp?.failed || [],
            fallback: false,
        };
    } catch (e) {
        if (!isMissingEndpoint(e)) {
            throw e;
        }
    }
    const result: SeriesCreateResult = {seriesId: null, created: [], failed: [], fallback: true};
    for (const date of dates) {
        try {
            const created = await EventManagerApi.createEventInfo({...base, startTime: date});
            result.created.push({_id: idString(created?._id), startTime: date});
        } catch (err) {
            result.failed.push({startTime: date, message: apiErrorMessage(err) || 'Unbekannter Fehler'});
        }
    }
    return result;
}
